import { List } from 'immutable';
import createCachedSelector from 're-reselect';
import { createSelector } from '@reduxjs/toolkit';

import type { ExtendedMatchEntity } from './types';
import type{ ReduxState } from 'app/redux/types';

import { getMatchGridTemplateByName } from 'app/configs/matchGridTemplates';
import { TWO_WAY_STAKE } from 'app/constants/StakeRenderer';
import type {
  RecordOfBetEntity,
  RecordOfSportEntity,
  RecordOfStakeEntity,
} from 'app/lib/betting/betting-entities';
import {
  BasketballPeriodType,
  IceHockeyPeriodType,
  SlotType,
  SoccerPeriodType,
  TennisPeriodType,
  VolleyballPeriodType,
} from 'app/lib/betting/betting-entities';
import createSelectors from 'app/lib/betting/betting-entities/selectors';
import { SportId } from 'app/lib/betting/constants';
import { orderByGlobalPriority } from 'app/lib/grid';
import { sortByPriority } from 'app/lib/sortByPriorityImmutableData';

export const {
  sports,
  sportIds,
  tournamentUnions,
  tournaments,
  matches,
  matchIds,
  objects,
  entityLoading,
  entityRequested,
  entityError,
  delta,
  isMatchStarted,
} = createSelectors(['search', 'matchesFound']);

const WINNING_BET_KEYS = [1, 2, 3];
const TWO_BETS = 2;
const ONE_BET = 1;

export const selectMatchFinishing = createCachedSelector(
  (_: ReduxState, matchId: number) => matchId,
  matches,
  (matchId, matches) => {
    const match = matches.get(matchId);

    if (!match) {
      return false;
    }

    const {
      sportId,
      periods,
    } = match;

    if (sportId === SportId.SOCCER) {
      const period = periods.get(SoccerPeriodType.MATCH);

      if (!period) {
        return false;
      }

      return period.finished;
    }

    if (sportId === SportId.BASKETBALL) {
      const period = periods.get(BasketballPeriodType.MATCH);

      if (!period) {
        return false;
      }

      return period.finished;
    }

    if (sportId === SportId.ICE_HOCKEY) {
      const period = periods.get(IceHockeyPeriodType.MATCH);

      if (!period) {
        return false;
      }

      return period.finished;
    }

    if (sportId === SportId.TENNIS) {
      const period = periods.get(TennisPeriodType.GAME);

      if (!period) {
        return false;
      }

      return period.finished;
    }

    if (sportId === SportId.VOLLEYBALL) {
      const period = periods.get(VolleyballPeriodType.MATCH);

      if (!period) {
        return false;
      }

      return period.finished;
    }

    return;
  },
)((_, matchId) => matchId);

export const selectWinningBets = createCachedSelector(
  (_: ReduxState, matchId: number) => matchId,
  matches,
  (matchId, matches) => {
    const winningBets: Array<null | RecordOfBetEntity> = [];
    const match = matches.get(matchId);

    if (!match) {
      return winningBets;
    }

    if (!match.bets?.size) {
      return winningBets;
    }

    const STAKE_RENDERER_CONF = getMatchGridTemplateByName('STAKE_RENDERER_CONF', null);
    const { bets } = match;

    if (match.stakes.size) {
      const stake: RecordOfStakeEntity = match.stakes.first();

      if (stake.type && STAKE_RENDERER_CONF?.[TWO_WAY_STAKE].includes(stake.type)) {
        if (bets.size === TWO_BETS) {
          winningBets.push(bets.first(null), null, bets.last(null));
        }

        if (bets.size === ONE_BET) {
          winningBets.push(bets.first(null), null, null);
        }

        return winningBets;
      }
    }

    const matchBets = bets.toList();

    WINNING_BET_KEYS.forEach((key) => {
      const bet = matchBets.find((bet) => bet.key === key);

      if (bet) {
        winningBets.push(bet);
      }
      else {
        winningBets.push(null);
      }
    });

    return winningBets;
  },
)((_, matchId) => matchId);

export const selectSortedMatches = createSelector(
  sports,
  tournamentUnions,
  tournaments,
  matches,
  (sports, unions, tournaments, matches) => {
    const sortedSports = sortByPriority(sports);
    const sortedUnions = sortByPriority(unions);
    const sortedTournaments = sortByPriority(tournaments);

    return sortedSports.reduce((
      sortedMatches: List<ExtendedMatchEntity>,
      sport: RecordOfSportEntity,
    ) => {
      const sportUnions = sortedUnions.filter((union) => (union.sportId === sport.id));

      sportUnions.forEach((union) => {
        const unionTournaments = sortedTournaments.filter((tournament) => (
          tournament.tournamentUnionId === union.id
        ));

        unionTournaments.forEach((tournament) => {
          const orderedMatchIds = orderByGlobalPriority(matches, [...tournament.matchIds]);
          const firstMatchId = orderedMatchIds[0];
          const matchesByTournament: ExtendedMatchEntity[] = [];

          orderedMatchIds.forEach((id) => {
            const match: ExtendedMatchEntity | undefined = matches.get(id);

            if (match && match.id === firstMatchId) {
              match.isFirst = true;
            }

            if (match) {
              matchesByTournament.push(match);
            }
          });

          const isSomeMatchFromLine = matchesByTournament.some((match) => (
            match.slotType === SlotType.PREMATCH
          ));

          if (isSomeMatchFromLine) {
            matchesByTournament[0].linkToLine = true;
          }

          sortedMatches = sortedMatches.concat(matchesByTournament);
        });
      });

      return sortedMatches;
    }, List());
  },
);
